import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { uniq } from 'lodash';
import type { Channel } from 'pusher-js';
import type Pusher from 'pusher-js';
import errorReporter from '@app/errorReporter';
import {
  buildPusherChannel,
  HEARTBEAT_PREFIX,
  APPOINTMENT_PREFIX,
  getUsernamesByIds,
} from 'utils';
import type { EnhancedAppointment } from '@bb/domain/dist/types';
import Swal from 'sweetalert2';
import type { OrgType } from '@bb/automaton/src/types';
import useViewDashAppointment from './useViewDashAppointment';

export type ViewingState = Record<string, string[]>;

interface AppointmentEvents {
  onStatusChange?: (args: any) => void;
  onFlagged?: (args: any) => void;
  onUrgencyUpdate?: (args: any) => void;
  onAnsweringSettingsToggled?: () => void;
  onCovid19Signed?: (args: any) => void;
}

const useAppointmentWatch = (
  pusher: Pusher | undefined,
  orgId: string | undefined,
  orgType: OrgType | 'brokerage' | 'answering' | 'mlsStaff' | undefined,
  hasAppointmentFeature: boolean,
  events?: AppointmentEvents,
) => {
  const [viewingState, setViewingState] = useState<ViewingState>({});
  const [appointmentChannel, setAppointmentChannel] = useState<Channel>();
  const appointmentChannelNameRef = useRef<string | null>(null);

  const viewDashAppointment = useViewDashAppointment();

  // Subscribe to org-level heartbeat channel
  useEffect(() => {
    if (!pusher || !orgId || !hasAppointmentFeature) return;

    const channelName = buildPusherChannel(HEARTBEAT_PREFIX, orgId);
    const heartbeatChannel = pusher.subscribe(channelName);

    heartbeatChannel.bind(
      'viewing_appointment_added',
      (data: Record<string, string[]>) => {
        setViewingState(prev => {
          const next = { ...prev };
          Object.entries(data).forEach(([appointmentId, userIds]) => {
            next[appointmentId] = next[appointmentId]
              ? uniq([...next[appointmentId], ...userIds])
              : userIds;
          });
          return next;
        });
      },
    );

    heartbeatChannel.bind(
      'viewing_appointment_removed',
      (data: Record<string, string[]>) => {
        setViewingState(prev => {
          const next = { ...prev };
          Object.entries(data).forEach(([appointmentId, userIds]) => {
            if (next[appointmentId]) {
              next[appointmentId] = next[appointmentId].filter(
                userId => !userIds.includes(userId),
              );
              if (!next[appointmentId].length) {
                delete next[appointmentId];
              }
            }
          });
          return next;
        });
      },
    );

    if (events?.onStatusChange) {
      heartbeatChannel.bind('appointment_status', events.onStatusChange);
    }
    if (events?.onFlagged) {
      heartbeatChannel.bind('appointment_flagged', events.onFlagged);
    }
    if (events?.onUrgencyUpdate) {
      heartbeatChannel.bind(
        'appointment_urgency_updates',
        events.onUrgencyUpdate,
      );
    }
    if (events?.onAnsweringSettingsToggled) {
      heartbeatChannel.bind(
        'answering_settings_toggled',
        events.onAnsweringSettingsToggled,
      );
    }
    if (events?.onCovid19Signed) {
      heartbeatChannel.bind(
        'appointment_covid19_signed',
        events.onCovid19Signed,
      );
    }

    heartbeatChannel.bind('pusher:subscription_error', (err: any) => {
      errorReporter.error(new Error(err.error));
    });

    // Fetch initial viewing state
    axios
      .get('/appointment/heartbeat/subscribe')
      .then(res => {
        setViewingState(res.data);
      })
      .catch(err => {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          window.localStorage.setItem('loggedOut', 'true');
          return;
        }
        errorReporter.error(err);
      });

    return () => {
      pusher.unsubscribe(channelName);
    };
  }, [pusher, orgId, hasAppointmentFeature]);

  // Subscribe/unsubscribe to individual appointment channel
  const subscribeAppt = useCallback(
    (id: string) => {
      if (!pusher || !id) return;
      const channelName = buildPusherChannel(APPOINTMENT_PREFIX, id);
      appointmentChannelNameRef.current = channelName;
      const channel = pusher.subscribe(channelName);
      setAppointmentChannel(channel);
    },
    [pusher],
  );

  const unsubscribeAppt = useCallback(() => {
    if (!pusher || !appointmentChannelNameRef.current) return;
    pusher.unsubscribe(appointmentChannelNameRef.current);
    appointmentChannelNameRef.current = null;
    setAppointmentChannel(undefined);
  }, [pusher]);

  const viewAppointment = async (appointment: EnhancedAppointment) => {
    if (!viewingState[appointment.id]?.length) {
      viewDashAppointment(appointment);
    } else {
      const viewingOrgText =
        orgType === 'brokerage'
          ? 'the MLS staff / answering service'
          : 'the brokerage';
      const usersViewing = await getUsernamesByIds(
        viewingState[appointment.id],
      );
      const filtered = usersViewing.filter(name => !!name);
      const title =
        filtered.length === 0
          ? `Someone from ${viewingOrgText} is already in this showing`
          : `${filtered.join(', ')} ${
              filtered.length === 1 ? 'is' : 'are'
            } already in this showing`;

      const { value } = await Swal.fire({
        type: 'warning',
        title,
        showCancelButton: true,
        cancelButtonText: 'Back to Queue',
        confirmButtonText: 'Enter Showing',
        confirmButtonColor: '#f48042',
      });

      if (value) {
        viewDashAppointment(appointment);
      }
    }
  };

  // Re-subscribe on reconnection
  useEffect(() => {
    if (!pusher) return;
    const onConnected = () => {
      if (appointmentChannelNameRef.current) {
        const channel = pusher.subscribe(appointmentChannelNameRef.current);
        setAppointmentChannel(channel);
      }
    };
    pusher.connection.bind('connected', onConnected);
    return () => {
      pusher.connection.unbind('connected', onConnected);
    };
  }, [pusher]);

  return {
    viewingState,
    appointmentChannel,
    subscribeAppt,
    unsubscribeAppt,
    viewAppointment,
  };
};

export default useAppointmentWatch;
